import { UnityFeatureType } from '@unity/clients';
import { QuickActionCallbackProps, QuickActionProps, QuickActionUrlProps } from '@unity/utils/types';

export class QuickActions {
  public static CreateBusinessPartnerUser = (agencyId: number): QuickActionUrlProps => {
    return {
      url: ['/business-partner', agencyId, 'user', 'create'],
      actionName: 'Create Business Partner User',
      description: 'Add a new user account linked to a business partner organization.',
      icon: 'userIcon',
      authorize: 'User:Create',
    };
  };

  public static CreateUser = (agencyId?: number): QuickActionUrlProps => {
    return {
      url: ['/user', 'create'],
      query: agencyId ? { agencyId } : undefined,
      actionName: 'Create Business Partner User',
      description: 'Add a new user account linked to a business partner organization.',
      icon: 'userIcon',
      authorize: 'User:Create',
    };
  };

  public static CreateInternalUser = (agencyId?: number): QuickActionUrlProps => {
    return {
      url: ['/user', 'create-internal'],
      query: agencyId ? { agencyId } : undefined,
      actionName: 'Create Internal User',
      description: 'Add a new user account for AHFC staff members.',
      icon: 'userIcon',
      authorize: 'InternalUser:Create',
    };
  };

  public static CreateRole = (agencyId: number): QuickActionUrlProps => {
    return {
      url: ['/business-partner', agencyId, 'role', 'create'],
      actionName: 'Create Role',
      description: 'Define a new role with specific system permissions.',
      icon: 'setColumnPositionIcon',
      feature: UnityFeatureType.RoleManagement,
      authorize: 'Role:Create',
    };
  };

  public static AssignUserToBusinessPartner = (userId: number): QuickActionUrlProps => {
    return {
      url: ['/user', userId, 'assign-role'],
      actionName: 'Assign User to a Business Partner',
      description: 'Link a user to a particular business partner organization.',
      icon: 'userIcon',
      authorize: 'UserRole:Create',
    };
  };

  public static CreateAgreementUser = (agreementId: number): QuickActionUrlProps => {
    return {
      url: ['/agreement', agreementId, 'assign-user'],
      actionName: 'Assign User to this Agreement',
      description: 'Grant a user access to work with a specific agreement.',
      icon: 'connectorIcon',
      authorize: 'Agreement:Update',
    };
  };

  public static ImpersonateUser = (callback: () => void): QuickActionCallbackProps => {
    return {
      actionName: 'Impersonate User',
      description: 'Access the system as another user to troubleshoot or support their activities.',
      icon: 'handIcon',
      callback: callback,
      authorize: 'Impersonation:Create',
    };
  };

  public static EndImpersonation = (callback: () => void): QuickActionProps => {
    return {
      actionName: 'Exit Impersonation',
      description: 'Return to your own account after impersonating another user.',
      icon: 'logoutIcon',
      callback: callback,
    };
  };

  public static LogOut = (callback: () => void): QuickActionCallbackProps => {
    return {
      actionName: 'Log Out',
      description: 'Log Out of current user to sign in.',
      icon: 'logoutIcon',
      callback: callback,
      authorize: 'Profile:View',
    };
  };

  public static CreateReferenceFile = (agreementId: number): QuickActionUrlProps => {
    return {
      url: ['/agreement', agreementId, 'reference-file', 'create'],
      actionName: 'Upload Reference File',
      description: 'Add supporting or reference material for system users.',
      icon: 'uploadIcon',
      authorize: 'ReferenceFile:Create',
    };
  };

  public static CreateDocument = (agreementId: number): QuickActionUrlProps => {
    return {
      url: ['/agreement', agreementId, 'document', 'create'],
      actionName: 'Create New Document',
      description: 'Upload a new document related to an agreement or client.',
      icon: 'fileIcon',
      authorize: 'Document:Create',
    };
  };

  public static CreateFormSchedule = (agreementId: number): QuickActionUrlProps => {
    return {
      url: ['/agreement', agreementId, 'form', 'create'],
      actionName: 'Create New Form Template',
      description: 'Build a reusable template that your team can version and use to collect data from business partners.',
      icon: 'plusIcon',
      authorize: 'FormSchedule:Create',
    };
  };

  public static CreateFormInstance = (agreementId: number, formScheduleId: number): QuickActionUrlProps => {
    return {
      url: ['/agreement', agreementId, 'form-instance', 'create'],
      query: { formScheduleId },
      actionName: 'Create Form',
      description: 'Make a form based on this form template available to business partners to complete.',
      icon: 'hyperlinkOpenIcon',
      authorize: 'FormSchedule:Create',
    };
  };

  public static ManageAgreement = (agreementId: number): QuickActionUrlProps => {
    return {
      url: ['/agreement', agreementId],
      actionName: 'Edit Agreement Details',
      description: 'Update or review details specific to an agreement.',
      icon: 'gearIcon',
      authorize: 'Agreement:Update',
    };
  };

  public static ReturnToDashboard: QuickActionUrlProps = {
    url: ['/user', 'dashboard'],
    actionName: 'Return to Dashboard',
    description: 'Return to the user dashboard.',
    icon: 'arrowLeftIcon',
    authorize: 'Profile:View',
  };

  public static ReturnToAgreement = (agreementId: number): QuickActionUrlProps => {
    return {
      url: ['/agreement', agreementId, 'view'],
      actionName: 'Return to Agreement',
      description: 'Return to manage Agreement.',
      icon: 'arrowLeftIcon',
      authorize: 'Agreement:View',
    };
  };

  public static ReturnToAgreementUsersList = (agreementId: number): QuickActionUrlProps => {
    return {
      url: ['/agreement', agreementId, 'user'],
      actionName: 'Return to Agreement Users',
      description: 'Return to Agreement Users List.',
      icon: 'arrowLeftIcon',
      authorize: 'Agreement:Update',
    };
  };

  public static ManageAgreements: QuickActionUrlProps = {
    url: ['/agreement'],
    actionName: 'View Agreements',
    description: 'Access agreements.',
    icon: 'eyeIcon',
    authorize: 'Agreement:List',
    authorizeOneOf: ['Agreement:Create', 'Agreement:Update', 'Agreement:Delete'],
  };

  public static ManageAgreementUser = (agreementId: number, userId: number): QuickActionUrlProps => {
    return {
      url: ['/agreement', agreementId, 'user', userId],
      actionName: 'Manage Agreement User',
      description: 'Update or review details specific to an agreement user.',
      icon: 'gearIcon',
      authorize: 'Agreement:Update',
    };
  };

  public static ListDocumentAndReferenceFiles = (agreementId: number): QuickActionUrlProps => {
    return {
      url: ['/agreement', agreementId, 'document'],
      actionName: 'View Documents and Files',
      description: 'Access offical attachments and supporting documents.',
      icon: 'binocularsIcon',
      authorize: 'Document:List',
    };
  };

  public static ManageFormTemplates = (agreementId: number): QuickActionUrlProps => {
    return {
      url: ['/agreement', agreementId, 'form'],
      actionName: 'View Form Templates',
      description: 'Access available form templates.',
      icon: 'binocularsIcon',
      authorize: 'FormSchedule:List',
      authorizeOneOf: ['FormSchedule:Create', 'FormSchedule:Update', 'FormSchedule:Delete'],
    };
  };

  public static CloneFormTemplate = (formSchemaId: number): QuickActionUrlProps => {
    return {
      url: ['/form-template', formSchemaId, 'clone'],
      actionName: 'Clone Form Template',
      description: 'Create a new form template based on this template.',
      icon: 'trackChangesEnableIcon',
      authorize: 'FormSchema:Create',
    };
  };

  public static CreateFormTemplate: QuickActionUrlProps = {
    url: ['/form-template', 'create'],
    actionName: 'Create Form Template',
    description: 'Create a new form template.',
    icon: 'trackChangesEnableIcon',
    authorize: 'FormSchema:Create',
  };

  public static ManageFormSchemas: QuickActionUrlProps = {
    url: ['/form-template'],
    actionName: 'Manage Form Templates',
    description: 'Manage available form templates.',
    icon: 'gearIcon',
    authorize: 'FormSchema:List',
    authorizeOneOf: ['FormSchema:Create', 'FormSchema:Update', 'FormSchema:Delete'],
  };

  public static ViewAgreement = (agreementId: number): QuickActionUrlProps => {
    return {
      url: ['/agreement', agreementId, 'view'],
      actionName: 'View Agreement',
      description: 'Open an agreement to review its details without editing.',
      icon: 'binocularsIcon',
      authorize: 'Agreement:View',
    };
  };

  public static ListFormsForFromSchedule = (agreementId: number, fromScheduleId: number, actionName: string): QuickActionUrlProps => {
    return {
      url: ['/agreement', agreementId, 'form-schedule', fromScheduleId, 'form'],
      actionName: `${actionName}`,
      description: `Access the list of ${actionName}.`,
      icon: 'binocularsIcon',
      authorize: 'FormInstance:List',
    };
  };

  public static CreateFormForFormSchedule = (agreementId: number, formScheduleId: number, actionName: string): QuickActionUrlProps => {
    return {
      url: ['/agreement', agreementId, 'form-schedule', formScheduleId, 'form', 'create'],
      actionName: `Create ${actionName}`,
      description: `Fill out a ${actionName}.`,
      icon: 'plusIcon',
      authorize: 'FormInstance:List',
    };
  };

  public static AmendDocument = (agreementId: number, parentId: number): QuickActionUrlProps => {
    return {
      actionName: 'Amend Document',
      description: 'Make changes or revisions to an existing document.',
      authorize: 'Document:Update',
      icon: 'replaceSingleIcon',
      url: ['/agreement', agreementId, 'document', 'create'],
      query: { agreementId, parentId },
    };
  };

  public static ManageAgreementUsers = (agreementId: number): QuickActionUrlProps => {
    return {
      url: ['/agreement', agreementId, 'user'],
      actionName: 'View Agreement Users',
      description: 'Access the list of agreement users',
      icon: 'binocularsIcon',
      authorize: 'Agreement:List',
      authorizeOneOf: ['Agreement:Create', 'Agreement:Update', 'Agreement:Delete'],
    };
  };

  public static CreateBusinessPartner: QuickActionUrlProps = {
    url: ['/business-partner', 'create'],
    actionName: 'Create Business Partner',
    description: 'Add a new organization as a business partner in the system.',
    icon: 'myspaceIcon',
    authorize: 'Agency:Create',
  };

  public static ManageBusinessPartners: QuickActionUrlProps = {
    url: ['/business-partner'],
    actionName: 'Manage Business Partners',
    description: 'List of Business Partners',
    icon: 'gearIcon',
    authorize: 'Agency:List',
    authorizeOneOf: ['Agency:Create', 'Agency:Update', 'Agency:Delete'],
  };

  public static CreateProgram: QuickActionUrlProps = {
    url: ['/program', 'create'],
    actionName: 'Create Program',
    description: 'Set up a new program to manage related agreements and activities.',
    icon: 'heartIcon',
    authorize: 'Program:Create',
  };

  public static CreateVendor: QuickActionUrlProps = {
    url: ['/vendor', 'create'],
    actionName: 'Create Vendor',
    description: 'Add a new vendor record.',
    icon: 'buildingsSolidIcon',
    authorize: 'Vendor:Create',
  };

  public static ManagePrograms: QuickActionUrlProps = {
    url: ['/program'],
    actionName: 'Manage Programs',
    description: 'Manage Programs',
    icon: 'gearIcon',
    authorize: 'Program:List',
    authorizeOneOf: ['Program:Create', 'Program:Update', 'Program:Delete'],
  };

  public static ManageVendors: QuickActionUrlProps = {
    url: ['/vendor'],
    actionName: 'Manage Vendors',
    description: 'Manage Vendors',
    icon: 'gearIcon',
    authorize: 'Vendor:List',
    authorizeOneOf: ['Vendor:Create', 'Vendor:Update', 'Vendor:Delete'],
  };

  public static ManageUsers: QuickActionUrlProps = {
    url: ['/user'],
    actionName: 'Manage Users',
    description: 'Manage Users',
    icon: 'gearIcon',
    authorize: 'User:List',
  };

  public static ListReports: QuickActionUrlProps = {
    url: ['/report'],
    actionName: 'Reports',
    description: 'Reports',
    icon: 'gearIcon',
    authorize: 'Report:List',
  };

  public static CreateAgreement: QuickActionUrlProps = {
    url: ['/agreement', 'create'],
    actionName: 'Create Agreement',
    description: 'Establish a new agreement between AHFC and a business partner.',
    icon: 'trackChangesEnableIcon',
    authorize: 'Agreement:Create',
  };

  public static CreateCommunity: QuickActionUrlProps = {
    url: ['/community', 'create'],
    actionName: 'Create Community',
    description: 'Create a community to represent a city or town within a defined area.',
    icon: 'globeIcon',
    authorize: 'Community:Create',
  };

  public static ManageCommunities: QuickActionUrlProps = {
    url: ['/community'],
    actionName: 'Manage Communities',
    description: 'Manage Communities',
    icon: 'gearIcon',
    authorize: 'Community:List',
    authorizeOneOf: ['Community:Create', 'Community:Update', 'Community:Delete'],
  };

  public static CreateClient = (agreementId: number): QuickActionUrlProps => {
    return {
      url: ['/agreement', agreementId, 'client', 'create'],
      actionName: `Create Client`,
      description: `Create a client who is recieving assistance as part of this agreement.`,
      icon: 'plusIcon',
      authorize: 'Client:Create',
    };
  };

  public static ListClients = (agreementId: number): QuickActionUrlProps => {
    return {
      url: ['/agreement', agreementId, 'client'],
      actionName: 'View Clients',
      description: 'Access the list of clients',
      icon: 'binocularsIcon',
      authorize: 'Client:List',
    };
  };

  public static CreateClientExpense = (agreementId: number): QuickActionUrlProps => {
    return {
      url: ['/agreement', agreementId, 'client-expense', 'create'],
      actionName: `Create Client Expense`,
      description: `Create an expense for paying funds to a client on behalf of this agreement.`,
      icon: 'plusIcon',
      authorize: 'Client:Create',
    };
  };

  public static ListClientExpenses = (agreementId: number): QuickActionUrlProps => {
    return {
      url: ['/agreement', agreementId, 'client-expense'],
      actionName: 'View Client Expenses',
      description: 'Access the list of client expenses',
      icon: 'binocularsIcon',
      authorize: 'ClientExpense:List',
    };
  };

  public static CreateDisbursement = (agreementId: number): QuickActionUrlProps => {
    return {
      url: ['/agreement', agreementId, 'disbursement', 'create'],
      actionName: `Create Disbursement`,
      description: `Create a disbursement for this agreement.`,
      icon: 'plusIcon',
      authorize: 'Disbursement:Create',
    };
  };

  public static ListDisbursement = (agreementId: number): QuickActionUrlProps => {
    return {
      url: ['/agreement', agreementId, 'disbursement'],
      actionName: 'View Disbursements',
      description: 'Access the list of disbursements',
      icon: 'binocularsIcon',
      authorize: 'Disbursement:List',
    };
  };
}
